import * as React from "react";
import { Save, Trash2, AlertTriangle, CheckCircle2, XCircle, Clock, User as UserIcon, Info } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import CustomFieldsBlock from "@/components/CustomFieldsBlock";
import EvidenceBlock from "@/components/evidence/EvidenceBlock";
import DetailDrawer from "@/components/DetailDrawer";
import { fmtCents, fmtTime, fmtDuration } from "@/lib/time";
import { toast } from "sonner";

/**
 * Booking drawer — create or edit a booking.
 * - Surfaces 409 booking_overlap inline (toast + inline red).
 * - Surfaces no_shift_cover as a NON-BLOCKING amber banner.
 * - Status transitions rendered per legal transitions (matches backend).
 */
const LEGAL = {
  pending:   ["confirmed", "cancelled", "no_show"],
  confirmed: ["completed", "cancelled", "no_show"],
  completed: [],
  cancelled: [],
  no_show:   [],
};

const STATUS_LABEL = {
  pending: "Pending", confirmed: "Confirmed", completed: "Completed",
  cancelled: "Cancelled", no_show: "No-show",
};

export default function BookingDrawer({
  open, onOpenChange, drawer, companyId, services, members, sites,
  defaultSiteId, canManage, onChanged,
}) {
  const isEdit = drawer?.kind === "edit";
  const booking = drawer?.booking;
  const [f, setF] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [conflict, setConflict] = React.useState(null); // {conflicting_booking_id, conflicting_starts_at}
  const [fetched, setFetched] = React.useState(null);   // full booking with no_shift_cover

  React.useEffect(() => {
    if (!open || !drawer) { setF(null); setConflict(null); setFetched(null); return; }
    if (isEdit && booking) {
      setF({
        site_id: booking.site_id,
        service_id: booking.service_id,
        member_id: booking.member_id || "",
        starts_at_local: toLocalInput(booking.starts_at),
        customer_name: booking.customer?.name || "",
        customer_phone: booking.customer?.phone || "",
        customer_email: booking.customer?.email || "",
        party_size: booking.party_size || "",
        notes: booking.notes || "",
        source: booking.source || "internal",
        custom: booking.custom || {},
      });
      // Refetch for no_shift_cover
      api.get(`/companies/${companyId}/bookings/${booking.id}`)
        .then(({data}) => setFetched(data))
        .catch(()=>{});
    } else {
      // NEW
      const now = new Date();
      now.setMinutes(0, 0, 0);
      now.setHours(now.getHours() + 1);
      setF({
        site_id: defaultSiteId || (sites[0]?.id || ""),
        service_id: services[0]?.id || "",
        member_id: "",
        starts_at_local: toLocalInput(now.toISOString()),
        customer_name: "",
        customer_phone: "",
        customer_email: "",
        party_size: "",
        notes: "",
        source: "internal",
        custom: {},
      });
    }
  }, [open, drawer, booking, isEdit, defaultSiteId, sites, services, companyId]);

  if (!drawer) return null;
  const svc = services.find((s) => s.id === f?.service_id);
  const noCover = isEdit && fetched?.no_shift_cover;

  async function save() {
    if (!f || busy) return;
    setBusy(true); setConflict(null);
    try {
      const starts = new Date(f.starts_at_local).toISOString();
      const customer = {
        name: f.customer_name.trim(),
        phone: f.customer_phone || null,
        email: f.customer_email || null,
      };
      if (isEdit) {
        const payload = {
          starts_at: starts,
          member_id: f.member_id || null,
          party_size: f.party_size ? Number(f.party_size) : null,
          notes: f.notes,
          customer,
          custom: f.custom || {},
        };
        const { data } = await api.patch(`/companies/${companyId}/bookings/${booking.id}`, payload);
        toast.success("Saved");
        setFetched(data);
      } else {
        const payload = {
          site_id: f.site_id,
          service_id: f.service_id,
          customer,
          starts_at: starts,
          member_id: f.member_id || null,
          party_size: f.party_size ? Number(f.party_size) : null,
          source: f.source,
          notes: f.notes,
          custom: f.custom || {},
        };
        const { data } = await api.post(`/companies/${companyId}/bookings`, payload);
        if (data.no_shift_cover) {
          toast.warning("Created — no shift covers this slot.");
        } else {
          toast.success("Created");
        }
      }
      onChanged && onChanged();
      onOpenChange(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "booking_overlap") {
        setConflict(d);
        toast.error("Overlap: assigned member has another booking in this window.");
      } else {
        toast.error(formatApiErrorDetail(d));
      }
    } finally { setBusy(false); }
  }

  async function transition(target) {
    if (!isEdit) return;
    if (!window.confirm(`Move booking to ${STATUS_LABEL[target] || target}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/bookings/${booking.id}/transition`,
                                       { target_status: target });
      toast.success("Updated");
      setFetched({ ...(fetched || {}), ...data });
      onChanged && onChanged();
      if (["completed","cancelled","no_show"].includes(target)) {
        onOpenChange(false);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  const currentStatus = fetched?.status || booking?.status || "pending";
  const legalTargets = isEdit ? (LEGAL[currentStatus] || []) : [];
  const frozen = ["completed", "cancelled", "no_show"].includes(currentStatus);

  return (
    <DetailDrawer open={open} onOpenChange={onOpenChange}
                  title={isEdit ? (fetched?.customer?.name || booking?.customer?.name) : "New booking"}
                  subtitle={isEdit ? `${fetched?.service_name || booking?.service_name || ''} · ${STATUS_LABEL[currentStatus] || currentStatus}` : "Create a booking"}
                  testid="booking-drawer">
      {!f ? null : (
        <div className="p-5 space-y-3">
          {isEdit && (
            <div className="flex flex-wrap gap-1.5">
              <StatusPill s={currentStatus}/>
              {fetched?.source === "plus" && (
                <span className="chip mono-num text-[10px]" style={{background:'#D1FAE5', color:'#0E766E'}}>
                  from plus
                </span>
              )}
            </div>
          )}

          {noCover && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2"
                 data-testid="booking-no-cover">
              <AlertTriangle size={14} className="text-amber-700 shrink-0 mt-0.5"/>
              <div className="text-[12px] text-amber-800">
                <b>no_shift_cover</b> — the assigned member has no published shift covering this slot.
                Booking is still valid; publish a shift or reassign.
              </div>
            </div>
          )}
          {conflict && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 flex items-start gap-2"
                 data-testid="booking-overlap-warn">
              <XCircle size={14} className="text-red-700 shrink-0 mt-0.5"/>
              <div className="text-[12px] text-red-800">
                <b>booking_overlap</b> — conflicting slot at
                <span className="mono-num"> {conflict.conflicting_starts_at?.slice(11,16)}</span>.
                Pick a different time or member.
              </div>
            </div>
          )}

          {/* Service + site (only editable at create) */}
          {!isEdit && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="micro-label">Site</label>
                <select className="field-input mt-1" value={f.site_id}
                        onChange={(e)=>setF(x=>({...x, site_id:e.target.value}))}
                        data-testid="booking-site">
                  {sites.map((s)=><option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="micro-label">Service</label>
                <select className="field-input mt-1" value={f.service_id}
                        onChange={(e)=>setF(x=>({...x, service_id:e.target.value}))}
                        data-testid="booking-service">
                  {services.map((s)=>(
                    <option key={s.id} value={s.id}>
                      {s.name} · {fmtDuration(s.duration_mins)} · {fmtCents(s.price_cents)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {isEdit && (
            <div className="rounded-md border border-brand-rule bg-brand-canvas px-3 py-2 text-[12px] flex items-center gap-2">
              <Info size={13} className="text-brand-muted"/>
              <span className="text-brand-ink font-semibold">{fetched?.service_name || booking?.service_name}</span>
              <span className="text-brand-muted">·</span>
              <span className="mono-num text-brand-muted">{fmtDuration(fetched?.service_duration_mins ?? booking?.service_duration_mins)}</span>
              <span className="text-brand-muted">·</span>
              <span className="mono-num text-brand-ink">{fmtCents(fetched?.service_price_cents ?? booking?.service_price_cents)}</span>
            </div>
          )}

          {/* When + member */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="micro-label">Starts at</label>
              <input type="datetime-local" className="field-input mt-1 mono-num"
                     value={f.starts_at_local}
                     disabled={frozen || !canManage}
                     onChange={(e)=>setF(x=>({...x, starts_at_local:e.target.value}))}
                     data-testid="booking-starts"/>
              {svc && (
                <div className="text-[10.5px] text-brand-muted mt-1">Ends approx <span className="mono-num">{estEnd(f.starts_at_local, svc.duration_mins)}</span></div>
              )}
            </div>
            <div>
              <label className="micro-label">Assigned member</label>
              <select className="field-input mt-1" value={f.member_id}
                      disabled={frozen || !canManage}
                      onChange={(e)=>setF(x=>({...x, member_id:e.target.value}))}
                      data-testid="booking-member">
                <option value="">— Unassigned —</option>
                {members.map((m)=><option key={m.user_id} value={m.user_id}>{m.name}</option>)}
              </select>
            </div>
          </div>

          {/* Customer */}
          <div className="rounded-md border border-brand-rule p-3 space-y-2">
            <div className="micro-label flex items-center gap-1"><UserIcon size={11}/> Customer</div>
            <input className="field-input" placeholder="Full name" value={f.customer_name}
                   disabled={frozen || !canManage}
                   onChange={(e)=>setF(x=>({...x, customer_name:e.target.value}))}
                   data-testid="booking-customer-name"/>
            <div className="grid grid-cols-2 gap-2">
              <input className="field-input" placeholder="Phone" value={f.customer_phone}
                     disabled={frozen || !canManage}
                     onChange={(e)=>setF(x=>({...x, customer_phone:e.target.value}))}
                     data-testid="booking-customer-phone"/>
              <input className="field-input" placeholder="Email (optional)" value={f.customer_email}
                     disabled={frozen || !canManage}
                     onChange={(e)=>setF(x=>({...x, customer_email:e.target.value}))}
                     data-testid="booking-customer-email"/>
            </div>
            <div>
              <label className="micro-label">Party size</label>
              <input type="number" min="1" className="field-input mt-1 mono-num" value={f.party_size}
                     disabled={frozen || !canManage}
                     onChange={(e)=>setF(x=>({...x, party_size:e.target.value}))}
                     data-testid="booking-party"/>
            </div>
          </div>

          <div>
            <label className="micro-label">Notes</label>
            <textarea className="field-input mt-1 !h-16" value={f.notes}
                      disabled={frozen || !canManage}
                      onChange={(e)=>setF(x=>({...x, notes:e.target.value}))}
                      data-testid="booking-notes"/>
          </div>

          {/* Phase 7 — company-defined custom fields */}
          <CustomFieldsBlock
            companyId={companyId}
            entity="bookings"
            values={f.custom || {}}
            disabled={frozen || !canManage}
            onChange={(next) => setF((x) => ({ ...x, custom: next }))}
            testidPrefix="booking-cf"
          />

          {/* Phase 9 — Evidence capture (edit-only, once the booking has an id) */}
          {isEdit && booking?.id && (
            <EvidenceBlock
              companyId={companyId}
              attachedType="booking"
              attachedId={booking.id}
              canEdit={canManage}
              testidPrefix="booking-evidence"
            />
          )}
          {/* Save row */}
          {canManage && !frozen && (
            <div className="pt-3 border-t border-brand-rule flex items-center gap-2">
              <button className="btn-violet" onClick={save} disabled={busy}
                      data-testid="booking-save">
                <Save size={13}/> {busy ? "…" : (isEdit ? "Save" : "Create booking")}
              </button>
            </div>
          )}

          {/* Transitions */}
          {isEdit && canManage && legalTargets.length > 0 && (
            <div className="pt-3 border-t border-brand-rule">
              <div className="micro-label mb-2">Status</div>
              <div className="flex flex-wrap gap-2">
                {legalTargets.map((t) => (
                  <button key={t}
                          onClick={() => transition(t)}
                          disabled={busy}
                          className={`btn-outline ${t==='confirmed'?'!text-brand-violet !border-brand-violet/40':''} ${t==='completed'?'!text-emerald-700 !border-emerald-300':''} ${['cancelled','no_show'].includes(t)?'!text-brand-coral !border-brand-coral/40':''}`}
                          data-testid={`booking-to-${t}`}>
                    {t === "confirmed" && <CheckCircle2 size={13}/>}
                    {t === "completed" && <CheckCircle2 size={13}/>}
                    {(t === "cancelled" || t === "no_show") && <XCircle size={13}/>}
                    Mark {STATUS_LABEL[t] || t}
                  </button>
                ))}
              </div>
            </div>
          )}
          {frozen && (
            <div className="pt-3 border-t border-brand-rule text-[11.5px] text-brand-muted flex items-center gap-1.5">
              <Clock size={12}/> Booking is <b className="mx-0.5">{STATUS_LABEL[currentStatus]}</b> — no further changes.
            </div>
          )}
        </div>
      )}
    </DetailDrawer>
  );
}

function StatusPill({ s }) {
  const map = {
    pending: "chip-amber", confirmed: "chip-violet", completed: "chip-emerald",
    cancelled: "chip-coral", no_show: "chip-coral",
  };
  return <span className={`chip ${map[s] || 'chip'} mono-num text-[10px]`}>{STATUS_LABEL[s] || s}</span>;
}

function toLocalInput(iso) {
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth()+1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}
function estEnd(localStart, duration) {
  if (!localStart) return "";
  const d = new Date(localStart);
  d.setUTCMinutes(d.getUTCMinutes() + Number(duration || 0));
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}
